/**
 * MACHINE-UX — Machine Overview facet (§5.2).
 * Identity / presence / group only. Never embeds Board / Forge / pins / command theatre.
 * Hardware owns board; Diagnostics owns health evidence.
 */
import { useEffect, useMemo, useState } from "react";
import { html, Alert, go, Card, KV } from "./ui.js";
import { path, useAction, useApi } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import { edgeDevicesOf } from "./edgeos-live.js";
import { sitesOfDevice } from "./ops-map-region-filter.js";
import ZoneMiniMap, { pointInZone } from "./zone-mini-map.js";

function rowOf(regData, deviceId) {
  const did = String(deviceId || "").trim();
  if (!did) return null;
  const rows = edgeDevicesOf(regData);
  return rows.find((r) => String(r.id || "").trim() === did) || null;
}

function zonesOf(pack) {
  const rows = (pack && (pack.items || pack.zones)) || [];
  return Array.isArray(rows)
    ? rows.filter((z) => z && Array.isArray(z.ring) && z.ring.length >= 3)
    : [];
}

export function MachineOverviewPanel({ deviceId, toast }) {
  const did = String(deviceId || "").trim();
  const posture = useApi(did ? path("get__fleet_device_status") : null);
  const post = posture.data || {};
  const writeOk = posture.loading ? false : (
    post.profile_write_allowed === true || post.write_allowed === true
  );
  const reg = useApi(did ? path("get__devices", { query: { limit: 80 } }) : null);
  const device = useMemo(() => rowOf(reg.data, did), [reg.data, did]);
  const raw = device && device.raw && typeof device.raw === "object" ? device.raw : {};
  const site = sitesOfDevice(raw)[0] || "";
  const zonesApi = useApi(
    did && site ? path("get__zones", { query: { site_id: site } }) : null,
  );
  const zones = useMemo(() => zonesOf(zonesApi.data), [zonesApi.data]);
  const act = useAction();
  const [zoneId, setZoneId] = useState("");
  const [point, setPoint] = useState(null);
  const [placementDirty, setPlacementDirty] = useState(false);

  const reload = () => {
    reg.reload && reg.reload();
    posture.reload && posture.reload();
    toast && toast("Overview re-read", "go");
  };

  const online = device ? !!device.online : false;
  const group = device ? String(device.group || "").trim() : "";
  const zone = String(raw.zone || "").trim();
  const savedPoint = raw.map_lng != null && raw.map_lat != null
    ? [Number(raw.map_lng), Number(raw.map_lat)]
    : null;
  const kind = device ? String(device.kind || "").trim() : "";
  const fw = device ? String(device.fw || "").trim() : "";
  const label = device ? String(device.label || device.id || "").trim() : did;
  const selectedZone = zones.find((z) => String(z.zone_id || z.id || "") === zoneId) || null;
  const pointInside = !!(selectedZone && pointInZone(point, selectedZone.ring));

  useEffect(() => {
    if (placementDirty) return;
    setPoint(savedPoint);
    const match = zones.find((z) => {
      const id = String(z.zone_id || z.id || "");
      const name = String(z.label || "");
      return id === zone || name === zone || pointInZone(savedPoint, z.ring);
    });
    setZoneId(match ? String(match.zone_id || match.id || "") : "");
  }, [device, zonesApi.data, placementDirty]);

  if (!did) {
    return html`<div data-cmc-mch="overview" data-cmc-shell="machine-ux">
      <${Alert} tone="warn" title="No machine selected"
        say="Pick a machine from the list. Empty is honest — nothing is invented here."/>
      <button type="button" className="btn btn--go btn--sm" onClick=${() => go("/machine")}>
        Back to list</button>
    </div>`;
  }

  const savePlacement = async () => {
    if (!writeOk) {
      toast && toast("Place edit closed for this seat", "warn");
      return;
    }
    if (!selectedZone || !point || !pointInside) {
      toast && toast("Place the machine inside the selected Zone first", "warn");
      return;
    }
    const out = await act.run(path("patch__devices_p_profile", { pathParams: [did] }), {
      method: "PATCH",
      body: {
        map_lng: Number(point[0]),
        map_lat: Number(point[1]),
        map_label: String(selectedZone.label || selectedZone.zone_id || selectedZone.id || ""),
      },
    });
    if (!out) {
      toast && toast(act.error || "Placement change refused", "bad");
      return;
    }
    setPlacementDirty(false);
    toast && toast("Machine placement saved · Zone will be derived from geometry", "go");
    reg.reload && reg.reload();
    zonesApi.reload && zonesApi.reload();
  };

  return html`<div data-cmc-mch="overview" data-cmc-shell="machine-ux" data-device=${did}
      data-cmc-machine-home=${site} data-cmc-zones-site=${site}
      data-cmc-soft-theatre="forbidden">
    ${act.envelope
      ? html`<${KV} k="Last action" v=${envelopeSummary(act.envelope) || "—"} tone=${act.envelope.ok ? "hi" : "warn"}/>`
      : null}
    <div className="btn-row" style=${{ flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
      <button type="button" className="btn btn--ghost btn--sm" onClick=${reload}
        disabled=${reg.loading}>Re-read</button>
    </div>

    ${reg.loading && !device
      ? html`<p className="note">Reading machine register…</p>`
      : null}
    ${reg.error
      ? html`<${Alert} tone="warn" title="Register unavailable"
          say="Could not confirm this machine from the account register. Re-read from the list, or try again."/>`
      : null}
    ${!reg.loading && !reg.error && !device
      ? html`<${Alert} tone="warn" title="Not on this account"
          say="This id is not in the register for your seat. Empty stays empty."/>`
      : null}

    ${device
      ? html`<div className="grid g2">
          <${Card} title="Identity" meta="from register">
            <${KV} k="Name" v=${label || "—"}/>
            <${KV} k="Id" v=${did}/>
            <${KV} k="Class" v=${kind || "—"} tone=${kind ? "" : "dim"}/>
            <${KV} k="Base" v=${fw || "—"} tone=${fw ? "" : "dim"}/>
          </${Card}>
          <${Card} title="Presence & place" meta=${online ? "online" : "quiet"}>
            <${KV} k="Presence" v=${online ? "Online" : "Quiet"} tone=${online ? "" : "bad"}/>
            <${KV} k="Group" v=${group || "—"} tone=${group ? "" : "dim"}/>
            <${KV} k="Site" v=${site || "—"} tone=${site ? "" : "dim"}/>
            <p className="note">Class and Base stay here — not stacked on the list. Commands stay on Diagnostics after confirm.</p>
          </${Card}>
        </div>
        <${Card} title="Zone placement" meta=${!site ? "no home" : (placementDirty ? "unsaved point" : "geometry-derived")} cls="mt">
          ${!site ? html`<p className="note" data-cmc-zones-closed="1">No home site. Zones stay closed until this machine has a home.</p>` : null}
          ${site && zonesApi.error ? html`<${Alert} tone="warn" title="Zones unavailable"
            say="The current machine facts remain visible, but placement cannot be changed until Zone geometry returns."/>` : null}
          <div className="machine-placement">
            <div className="machine-placement__controls">
              <label className="field"><span>Preview Zone</span>
                <select className="inp" value=${zoneId} onChange=${(event) => setZoneId(event.target.value)}>
                  <option value="">Choose a surveyed Zone</option>
                  ${zones.map((z) => html`<option key=${z.zone_id || z.id}
                    value=${String(z.zone_id || z.id || "")}>${z.label || z.zone_id || z.id}</option>`)}
                </select>
              </label>
              <p className="note">Zone is not a free-text assignment. Select a boundary, then click the mini map to save this machine's real coordinate inside it.</p>
              <div className="machine-placement__status" data-tone=${pointInside ? "go" : "warn"}>
                <span>${pointInside ? "Point is inside selected Zone" : "Choose a Zone and place a point inside it"}</span>
                <b>${point ? `${Number(point[1]).toFixed(5)}, ${Number(point[0]).toFixed(5)}` : "No saved coordinate"}</b>
              </div>
              <button type="button" className="btn btn--go btn--sm"
                disabled=${!writeOk || !placementDirty || !pointInside || act.busy} onClick=${savePlacement}>
                ${act.busy ? "Saving…" : "Save placement"}
              </button>
              ${!writeOk
                ? html`<p className="note">Place edit closed for this seat.</p>`
                : null}
            </div>
            <${ZoneMiniMap} zones=${zones} selectedId=${zoneId} point=${point}
              interactive=${!!selectedZone}
              onPoint=${(next) => {
                if (!selectedZone || !pointInZone(next, selectedZone.ring)) {
                  toast && toast("That point is outside the selected Zone", "warn");
                  return;
                }
                setPoint(next);
                setPlacementDirty(true);
              }}/>
          </div>
        <//>`
      : null}
  </div>`;
}

export default MachineOverviewPanel;
